import { Fragment, useEffect, useState } from 'react';
import { Root } from '@radix-ui/react-dialog';
import styled from 'styled-components';
import tw from 'twin.macro';

import HouseImage from '@/assets/images/image-house.webp';
import useAdsRef from '@/api/mutations/useAdsRef';
import useUser from '@/api/queries/useUser';
import {
  AdsTable,
  Button,
  HomeTypeTagContainer,
  Dialog,
  LoadingOverlay,
  NoDataInfo,
  TableHeader,
  TooltipInfo,
  WeightChip,
} from '@/components';
import AdvancedFilter from '../../components/AdvancedFilter';
import Filters from '@/components/Filters';
import { useFilterContext } from '@/context/FilterContext';
import { useMapContext } from '@/context/MapContext';
import { useProjectContext } from '@/context/ProjectContext';
import {
  ButtonColor,
  ButtonSize,
  Icons,
  SearchParams,
  TabsType,
  OwnershipType,
  HomeTypeEnum,
} from '@/enums';
import { filterItems, weightItems } from './filterObjects';
import { getMapMarkerDataFromAds } from '@/helpers';
import strings from '@/language/Swedish';
import {
  ContentContainer,
  WeightContainer,
  InnerContainer,
  TableContainer,
  TopRightWrapper,
  ButtonContainer,
  HideButton,
} from '@/styles/MapAttributesStyles';
import {
  AdsRefDataType,
  AdsWeightDataType,
  CoordinatesType,
} from '@/types/dataTypes';
import { ANALYTICS_ROLE, DISTANCE_STEPS_ADS } from '@/constants';
import { getAdsApiParams } from '@/api/apiHelpers';
import { defaultCoordinates } from '@/context/default';
import { IndexResponseType } from '@/types/apiTypes';
import useIsMobile from '@/helpers/useIsMobile';

const StyledAds = styled.div`
  ${tw`flex h-full overflow-hidden`};
`;

const Ads = () => {
  const [isResetDialogOpen, setIsResetDialogOpen] = useState<boolean>(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState<boolean>(false);
  const [isWeightChipOpen, setIsWeightChipOpen] = useState<boolean>(false);
  const [distanceStep, setDistanceStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [init, setInit] = useState(true); // Prevent rerender cause of cordinates is set several times when project is loading (4 times)
  const isMobile = useIsMobile();
  const { data: userData } = useUser();

  const hasAnalyticsRole = userData
    ? userData.roles.includes(ANALYTICS_ROLE)
    : false;

  const {
    currentCoordinates,
    isStreetViewOn,
    isMapVisible,
    setCoordinates,
    setAdsMarkerObjects,
  } = useMapContext();
  const {
    project: {
      adsTableData,
      adsTableIds,
      referencePrice,
      adsTableColumns,
      adsSummary,
      adsPolygons,
      indexValues
    },
    isAdvancedFilterOpen,
    setProject,
    updateAdsTableData,
    toggleColumnVisibility,
  } = useProjectContext();

  const {
    adsData: adsFilterData,
    isFilterHidden,
    resetSoldAndAdParams,
    setSoldAndAdParam,
    setAdsParam,
    setIsFilterHidden,
    setMarketParam
  } = useFilterContext();

  const params = getAdsApiParams(adsFilterData);
  const { mutate: updateAdsDataMutation, data: searchData, isError: isError, isPending: isSearchPending } = useAdsRef();

  function refetch(step: number) {
    updateAdsDataMutation({
      coordinates: currentCoordinates,
      parameters: params,
      adsTableIds: adsTableIds,
      polygonCoordinates: adsPolygons,
      distance: DISTANCE_STEPS_ADS[step]
    });
  }

  useEffect(() => {
    if (!isAdvancedFilterOpen && !isFilterDialogOpen && !isWeightChipOpen && !init) {
      setDistanceStep(0);
      setLoading(true);
      refetch(0);
    }
  }, [isAdvancedFilterOpen, isFilterDialogOpen, adsFilterData, isWeightChipOpen]);

  useEffect(() => {
    if (init && currentCoordinates !== defaultCoordinates) {
      refetch(0);
      setInit(false);
    }
    else if (!init && currentCoordinates !== defaultCoordinates) {
      setDistanceStep(0);
      setLoading(true);
      refetch(0);
    }
  }, [currentCoordinates, adsPolygons]);

  useEffect(() => {
    if (!init) {
      setLoading(true);
      setDistanceStep(0);
      refetch(0);
    }
  }, [adsTableIds.excluded]);

  useEffect(() => {
    if (!init && (searchData?.ads?.length || 0) < (adsFilterData.filterData.limit * 0.8) && distanceStep < DISTANCE_STEPS_ADS.length - 1 && !isSearchPending) {
      refetch(distanceStep + 1);
      setDistanceStep((prevState) => prevState + 1);
    } else if (searchData) {
      updateAdsTableData(searchData);

      if (searchData.ads.length > 0) {
        const mapMarkerData = getMapMarkerDataFromAds(searchData.ads);

        setAdsMarkerObjects(mapMarkerData);
      } else {
        setAdsMarkerObjects(null);
      }
      setLoading(false);
      setInit(false);
    }

  }, [searchData]);

  const handleWeightChange = (keyEl: string, value: number) => {
    setSoldAndAdParam(SearchParams.weight, keyEl, value);
  };

  const handleRefValueChange = (keyEl: string, value: number) => {
    setSoldAndAdParam(SearchParams.ref, keyEl, value);
  };

  const handleLimitChange = (value: string | number) => {
    const limitValue = parseInt(value.toString());
    setSoldAndAdParam(SearchParams.filter, 'limit', limitValue);
  };

  const handleAdvancedSearch = (coordinates?: CoordinatesType) => {
    if (coordinates) setCoordinates(coordinates);
  };

  useEffect(() => {
    if (!isAdvancedFilterOpen) {
      const updatedIndexValues: IndexResponseType = {
        ...indexValues as IndexResponseType,
        home_type: ((adsFilterData?.filterData?.home_type?.includes(HomeTypeEnum.house) || !adsFilterData?.filterData?.home_type) && !adsFilterData?.filterData?.ownership_type?.includes(OwnershipType.coOperativeApartment) && adsFilterData?.filterData?.ownership_type) ? HomeTypeEnum.house : HomeTypeEnum.apartment
      }
      setProject((prevState) => ({
        ...prevState,
        indexValues: updatedIndexValues
      }));
    }
  }, [isAdvancedFilterOpen]);



  return (
    <StyledAds>
      <AdvancedFilter
        setMarketParam={setMarketParam}
        handleSearch={handleAdvancedSearch}
        filterData={adsFilterData}
        setMultipleParam={setSoldAndAdParam}
        handleResetFilter={() => resetSoldAndAdParams(SearchParams.all)}
      />
      <>
        <WeightContainer $isHidden={isFilterHidden || isStreetViewOn || isMobile}>
          <InnerContainer>
            <div className="hidden sm:flex w-full flex-wrap gap-1">
              {weightItems.map((item, index) => (
                <Fragment key={item.keyWeightEl}>
                  <WeightChip
                    setIsWeightChipOpen={setIsWeightChipOpen}
                    key={item.keyWeightEl}
                    weightValue={
                      adsFilterData.weightData[
                      item.keyWeightEl as keyof AdsWeightDataType
                      ]
                    }
                    refValue={
                      adsFilterData.refData[
                      item.keyRefEl as keyof AdsRefDataType
                      ] || undefined
                    }
                    icon={item.icon}
                    inputType={item.inputType}
                    inputMin={item.min}
                    inputMax={item.max}
                    keyWeightEl={item.keyWeightEl}
                    keyRefEl={item.keyRefEl}
                    label={item.label}
                    unit={item.unit}
                    onUpdateWeight={handleWeightChange}
                    onUpdateRefValue={handleRefValueChange}
                  />
                  {index % 4 === 3 && <div className="basis-full"></div>}
                </Fragment>
              ))}
              <Button
                icon={Icons.refresh}
                color={ButtonColor.light}
                size={ButtonSize.small}
                onClick={() => setIsResetDialogOpen(true)}
              >
                {strings.data.reset}
                </Button>
            </div>
            <Root open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
              <Dialog
                title={`${strings.dashboard.resetWarningHeading}`}
                description={`${strings.dashboard.resetWarning}`}
                bottomButton={{
                  text: strings.dashboard.resetWarningButton,
                  type: 'submit',
                  onClick: () => resetSoldAndAdParams(SearchParams.weightRef),
                }}
              />
            </Root>
          </InnerContainer>
        </WeightContainer>
        <TopRightWrapper>
          <ButtonContainer>
            <Filters
              setCoordinates={setCoordinates}
              setIsOpen={setIsFilterDialogOpen}
              handleResetFilter={() => resetSoldAndAdParams(SearchParams.filter)}
              handleClose={() => {
                refetch(0);
              }}
              filterData={adsFilterData.filterData}
              setParam={setAdsParam}
              setMultipleParam={setSoldAndAdParam}
              filterItems={filterItems}
              isOpen={isFilterDialogOpen}
              isFilterHidden={isFilterHidden}
            />
            <TooltipInfo content={strings.dashboard.hide}>
              <div>
                <HideButton
                  icon={Icons.arrowUp}
                  color={isFilterHidden ? ButtonColor.primary : ButtonColor.light}
                  size={ButtonSize.large}
                  $isHidden={isFilterHidden}
                  onClick={() => setIsFilterHidden((prevState) => !prevState)}
                />
              </div>
            </TooltipInfo>
          </ButtonContainer>
          <HomeTypeTagContainer filterData={adsFilterData.filterData} />
        </TopRightWrapper>
      </>
      <ContentContainer $isFilterHidden={isFilterHidden} $isMapVisible={isMapVisible}>
        {loading && <LoadingOverlay />}
        {!isError && adsTableData ? (
          <TableContainer>
            <TableHeader
              limit={adsFilterData.filterData.limit}
              hasExcludedIds={
                adsTableIds.excluded && adsTableIds.excluded.size > 0
                  ? true
                  : false
              }
              nrOfObjects={adsTableData.length}
              nrOfSelectedObjects={adsTableIds?.selected?.size || 0}
              userRefSqm={adsFilterData.refData.living_area_reference}
              referencePrice={referencePrice}
              columns={adsTableColumns}
              isAuthorized={hasAnalyticsRole}
              handleLimitChange={handleLimitChange}
              resetExcludedIds={() =>
                setProject((prevState) => ({
                  ...prevState,
                  adsTableIds: { ...prevState.adsTableIds, excluded: null },
                }))
              }
              setColumns={(columns) =>
                setProject((prevState) => ({
                  ...prevState,
                  adsTableColumns: columns,
                  adsTableReportColumns: columns,
                }))
              }
              toggleColumns={(id: string) =>
                toggleColumnVisibility(TabsType.ads, id)
              }
            />
            <AdsTable tableData={adsTableData} tableSummary={adsSummary} />
          </TableContainer>
        ) : (
          <NoDataInfo
            heading={
              isError
                ? strings.data.somethingWentWrong
                : strings.table.noDataHeading
            }
            altText={strings.table.noDataHeading}
            text={isError ? strings.data.error : strings.table.noDataText}
            img={HouseImage}
          />
        )}
      </ContentContainer>
    </StyledAds>
  );
};

export default Ads;
